import React, { useEffect, useState, useContext, useRef } from "react"
import PropTypes from "prop-types"

import Disenrollment from "./Disenrollment"

import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { InputMask } from "primereact/inputmask"
import { Messages } from "primereact/messages"

import { UserContext } from "../../../../context/userContext"

import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  multiFactor,
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth"

import styles from "../../../../styles/TwoFA.module.css"
import { getAuthErrorMessage } from "../../../../helpers/authErrorMessage"
import { LogError } from "../../../../helpers/logger"
import { LoaderProgressBar } from "../../../../components/Loaders"
import RefreshLogin from "./RefreshLogin"
import {
  clearRecaptcha,
  getRecaptcha,
  waitForRecaptchaReady,
} from "../../../../hooks/captcha/recaptchaSingleton"

export default function Sms({
  reloadUser,
  setEnableMfaEnrolledFactor,
  enrolledFactor,
  finalizationState,
  recaptchaVerifier,
  recaptchaInitError,
  recaptchaReady,
}) {
  const msgs = useRef(null)
  const userCtx = useContext(UserContext)

  const userPhone = userCtx?.session?.user?.staff_phone_number || ""
  const cleanedPhone = userPhone.replace(/^\+1/, "") // Removes +1 from the beginning

  const [phoneNumber, setPhoneNumber] = useState(cleanedPhone)
  const [verificationID, setVerificationID] = useState("")
  const [verificationCode, setVerificationCode] = useState("")
  const [verifySMSCode, setVerifySMSCode] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [refreshLogin, setRefreshLogin] = useState(false)
  const [loading, setLoading] = useState(false)
  const smsRequestIdRef = useRef(0)
  const smsRequestDeadlineTimeoutRef = useRef(null)

  const auth = getAuth()
  const user = auth.currentUser
  const RECAPTCHA_REQUEST_TIMEOUT_MS = 30000

  const invalidVerifierRegex =
    /invalid application verifier|reCAPTCHA token response is either invalid or expired|captcha check failed|closed by the user|cancelled|dismissed|aborted/i

  const isInvalidVerifierError = (error) =>
    error?.code === "auth/invalid-app-credential" ||
    error?.code === "auth/captcha-check-failed" ||
    error?.code === "auth/recaptcha-request-timeout" ||
    error?.code === "auth/recaptcha-render-timeout" ||
    invalidVerifierRegex.test(error?.message || "")

  const getReadyRecaptchaVerifier = async () => {
    let activeRecaptchaVerifier = recaptchaVerifier

    try {
      activeRecaptchaVerifier = getRecaptcha(auth) || recaptchaVerifier
      await waitForRecaptchaReady(auth, RECAPTCHA_REQUEST_TIMEOUT_MS)
    } catch (error) {
      LogError("Failed to acquire ready reCAPTCHA verifier for SMS request:", error)
      throw error
    }

    return activeRecaptchaVerifier
  }

  const clearSmsRequestDeadline = (requestId = null) => {
    // Only the active request can clear its own timer.
    if (
      requestId !== null &&
      smsRequestIdRef.current !== requestId
    ) {
      return
    }

    if (smsRequestDeadlineTimeoutRef.current) {
      clearTimeout(smsRequestDeadlineTimeoutRef.current)
      smsRequestDeadlineTimeoutRef.current = null
    }
  }

  const releaseSmsRequestBusyState = (requestId = null) => {
    // Guard state updates so stale requests cannot unlock newer retries.
    if (
      requestId !== null &&
      smsRequestIdRef.current !== requestId
    ) {
      return false
    }

    // Release both resend and reauth loaders so the setup form can recover.
    setIsResending(false)
    setLoading(false)
    return true
  }

  useEffect(() => {
    return () => {
      smsRequestIdRef.current += 1
      clearSmsRequestDeadline()
    }
  }, [])

  const clearInlineMessage = () => {
    msgs.current?.clear()
  }

  const notifyInfo = (detail) => {
    clearInlineMessage()
    userCtx?.setNotificationInformation(detail)
  }

  const showError = (detail) => {
    clearInlineMessage()
    msgs.current?.show([
      {
        severity: "error",
        detail,
        sticky: true,
        closable: true,
      },
    ])
  }

  const requestSmsVerification = async (activeRecaptchaVerifier) => {
    const multiFactorSession = await multiFactor(user).getSession()
    const phoneAuthProvider = new PhoneAuthProvider(auth)

    const formattedNumber = `+1${phoneNumber.replace(/\D/g, "")}`

    return phoneAuthProvider.verifyPhoneNumber(
      {
        phoneNumber: formattedNumber,
        session: multiFactorSession,
      },
      activeRecaptchaVerifier,
    )
  }

  const reauthenticate = async (password) => {
    try {
      setLoading(true)
      const credential = EmailAuthProvider.credential(user.email, password)
      await reauthenticateWithCredential(user, credential)

      setRefreshLogin(false)
      await handleSendVerification()
    } catch (error) {
      LogError("Error during re-authentication:", error)
      showError(
        "Re-authentication failed. Please refresh the page and try again.",
      )
    } finally {
      setLoading(false)
    }
  }

  const handleSendVerification = async () => {
    if (isResending) return

    const retryControlLabel = verifySMSCode
      ? "Resend Code"
      : "Verify Phone Number"

    const requestId = smsRequestIdRef.current + 1
    smsRequestIdRef.current = requestId

    clearSmsRequestDeadline()
    smsRequestDeadlineTimeoutRef.current = setTimeout(() => {
      // If verifyPhoneNumber hangs (for example challenge dismissed),
      // force this request to end so the user can retry.
      if (!releaseSmsRequestBusyState(requestId)) return

      // Invalidate this request and clear reCAPTCHA so users can retry.
      smsRequestIdRef.current += 1
      clearSmsRequestDeadline()
      clearRecaptcha()
      showError(
        `Security verification timed out. Please click ${retryControlLabel} to try again.`,
      )
    }, RECAPTCHA_REQUEST_TIMEOUT_MS)

    try {
      setIsResending(true)
      let verificationID = null
      let sendError = null

      for (let attempt = 1; attempt <= 2; attempt += 1) {
        const activeRecaptchaVerifier = await getReadyRecaptchaVerifier()

        if (smsRequestIdRef.current !== requestId) {
          return
        }

        if (!activeRecaptchaVerifier) {
          showError(
            "Security verification is unavailable. Please refresh and try again.",
          )
          return
        }

        try {
          verificationID = await requestSmsVerification(activeRecaptchaVerifier)

          if (smsRequestIdRef.current !== requestId) {
            return
          }

          sendError = null
          break
        } catch (error) {
          if (smsRequestIdRef.current !== requestId) {
            return
          }

          sendError = error

          if (error.code === "auth/requires-recent-login") {
            throw error
          }

          if (isInvalidVerifierError(error) && attempt === 1) {
            clearRecaptcha()
            continue
          }

          throw error
        }
      }

      if (!verificationID && sendError) {
        throw sendError
      }

      setVerificationID(verificationID)
      setVerifySMSCode(true)

      notifyInfo("Verification code sent.")
    } catch (error) {
      if (smsRequestIdRef.current !== requestId) {
        return
      }

      LogError("Error sending SMS:", error.code, error.message)
      if (error.code === "auth/requires-recent-login") {
        setRefreshLogin(true)
        return
      }

      if (isInvalidVerifierError(error)) {
        clearRecaptcha()
        showError(
          `Security verification was closed, canceled, or expired. Please click ${retryControlLabel} to try again.`,
        )
        return
      }

      showError(
        getAuthErrorMessage(error, "Failed to send SMS verification code."),
      )
    } finally {
      // Cleanup only for the active request to avoid clobbering retries.
      if (releaseSmsRequestBusyState(requestId)) {
        clearSmsRequestDeadline(requestId)
      }
    }
  }

  const handleVerifyCode = async () => {
    let verificationSucceeded = false
    setSubmitting(true)
    try {
      const cred = PhoneAuthProvider.credential(
        verificationID,
        verificationCode,
      )

      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
      await multiFactor(user).enroll(multiFactorAssertion, "Phone number")

      setVerifySMSCode(false)
      setPhoneNumber("")

      await setEnableMfaEnrolledFactor("phone")
      verificationSucceeded = true

      notifyInfo("Two-factor authentication enabled successfully.")
    } catch (error) {
      LogError("Error enrolling second factor:", error)
      showError(
        getAuthErrorMessage(
          error,
          "Failed to enable two-factor authentication.",
        ),
      )
    } finally {
      let reloadSucceeded = false
      try {
        await reloadUser()
        reloadSucceeded = true
      } catch (reloadError) {
        LogError("Error reloading user after SMS verification:", reloadError)
        if (verificationSucceeded) {
          showError(
            "Verification completed, but we could not refresh this page. Please refresh and check your 2FA status.",
          )
        }
      }

      if (!verificationSucceeded || !reloadSucceeded) {
        setSubmitting(false)
      }
    }
  }

  const handlePhoneNumberChange = (e) => {
    const nextPhoneNumber = e.target.value
    const phoneChanged = nextPhoneNumber !== phoneNumber

    setPhoneNumber(nextPhoneNumber)

    if (!phoneChanged) return

    setVerifySMSCode(false)
    setVerificationCode("")
    setVerificationID("")
  }

  const handleRetryBackendFinalize = async () => {
    setSubmitting(true)
    try {
      await setEnableMfaEnrolledFactor("phone")
      await reloadUser()
      notifyInfo("Account setup completed for SMS two-factor authentication.")
    } catch (error) {
      showError(
        getAuthErrorMessage(
          error,
          "Unable to complete account setup. Please try again.",
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (enrolledFactor) {
    const phoneLastDigits = enrolledFactor?.phoneNumber?.slice(-4)
    const enrollementDate = new Date(
      enrolledFactor.enrollmentTime,
    ).toDateString()
    return (
      <>
        <div className={styles.setupBlock}>
          <Messages ref={msgs} />
          <h3>✅ SMS Two-factor Authentication already enabled</h3>
          <p>
            Enrolled on: <strong>{enrollementDate}</strong>
          </p>
          <p>
            Phone number ending in: <strong>{phoneLastDigits}</strong>
          </p>
          {finalizationState?.pending && (
            <>
              <p className={styles.inlineErrorText}>
                Account setup is not complete yet. You can retry finalization without reenrolling this factor.
              </p>
              {finalizationState?.error ? (
                <p className={styles.inlineErrorText}>{finalizationState.error}</p>
              ) : null}
              <Button
                type="button"
                label="Retry setup"
                onClick={handleRetryBackendFinalize}
                loading={submitting}
                disabled={submitting}
                className={styles.primaryActionButton}
              />
            </>
          )}
        </div>
        <Disenrollment factorId={"phone"} />
      </>
    )
  }

  if (loading || refreshLogin) {
    return (
      <div className={styles.setupBlock}>
        <LoaderProgressBar />
        <Messages ref={msgs} />
        {refreshLogin && (
          <RefreshLogin
            onHide={(password) => {
              if (password) {
                reauthenticate(password)
              } else {
                setRefreshLogin(false)
              }
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className={styles.setupBlock}>
      <Messages ref={msgs} />
      <h3>Configure your SMS Authentication</h3>
      <div className={styles.block}>
        <p>Enter your phone number to enable SMS authentication:</p>
        <InputMask
          value={phoneNumber}
          disabled={isResending || submitting}
          onChange={handlePhoneNumberChange}
          mask="(999) 999-9999"
          placeholder="(123) 456-7890"
          slotChar="_"
        />
        <Button
          type="button"
          label="Verify Phone Number"
          onClick={handleSendVerification}
          loading={isResending}
          className={styles.primaryActionButton}
          disabled={
            verifySMSCode ||
            isResending ||
            submitting ||
            !recaptchaReady ||
            !!recaptchaInitError
          }
        />
        {!recaptchaReady && !recaptchaInitError && (
          <div>
            <small>Preparing security verification. Please wait...</small>
          </div>
        )}
        {recaptchaInitError && (
          <div>
            <small className={styles.inlineErrorText}>
              Security verification failed to initialize. Refresh the page and
              try again.
            </small>
          </div>
        )}
      </div>

      {verifySMSCode && (
        <div className={styles.block}>
          <span className="p-float-label">
            <InputText
              id="verificationCode"
              value={verificationCode}
              disabled={isResending || submitting}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
            <label>Verification Code</label>
          </span>
          <Button
            type="button"
            label="Enter Verification Code"
            onClick={handleVerifyCode}
            loading={submitting}
            className={`${styles.marginTop10} ${styles.primaryActionButton}`}
            disabled={
              isResending || submitting || verificationCode.trim() === ""
            }
          />
          <p className={styles.marginTop5}>
            Didn't receive a code?{" "}
            <Button
              type="button"
              label="Resend Code"
              text
              loading={isResending}
              onClick={handleSendVerification}
              disabled={submitting || isResending}
              className={styles.textButton}
            />
          </p>
        </div>
      )}
    </div>
  )
}

Sms.propTypes = {
  reloadUser: PropTypes.func.isRequired,
  setEnableMfaEnrolledFactor: PropTypes.func.isRequired,
  enrolledFactor: PropTypes.object,
  finalizationState: PropTypes.shape({
    pending: PropTypes.bool,
    error: PropTypes.string,
  }),
  recaptchaVerifier: PropTypes.object,
  recaptchaInitError: PropTypes.object,
  recaptchaReady: PropTypes.bool,
}
